import React, { useState, useEffect } from 'react';
import { useSubmitLock } from '../hooks/useSubmitLock';
import { useNotification } from '../contexts/NotificationContext';
import { rbacService } from '../services/rbacService';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonOff as PersonOffIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';

const UserManagement = () => {
  const [submitting, runSubmit] = useSubmitLock();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role_id: '',
    is_active: true
  });
  const { showNotification } = useNotification();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, rolesData] = await Promise.all([
        rbacService.getUsers(),
        rbacService.getRoles()
      ]);
      setUsers(usersData);
      setRoles(rolesData);
    } catch (err) {
      setError(err.message);
      showNotification('Erro ao carregar dados', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    setSelectedUser(user);
    if (user) {
      setFormData({
        email: user.email,
        full_name: user.full_name,
        password: '',
        role_id: user.role_id || '',
        is_active: user.is_active
      });
    } else {
      setFormData({
        email: '',
        full_name: '',
        password: '',
        role_id: '',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleForceLogout = async (userId, userName) => {
    if (window.confirm(`Tem certeza que deseja forçar o logout de "${userName}"?\n\nIsso irá invalidar todas as sessões ativas do usuário e ele precisará fazer login novamente para atualizar suas permissões.`)) {
      try {
        await rbacService.forceUserLogout(userId);
        showNotification('Logout forçado com sucesso. O usuário precisará fazer login novamente.', 'success');
        loadData();
      } catch (err) {
        showNotification(err.response?.data?.detail || 'Erro ao forçar logout', 'error');
      }
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setFormData({
      email: '',
      full_name: '',
      password: '',
      role_id: '',
      is_active: true
    });
  };

  const handleSaveUser = async () => {
    try {
      if (selectedUser) {
        // Atualizar usuário
        const updateData = {
          full_name: formData.full_name,
          role_id: formData.role_id || null,
          is_active: formData.is_active
        };
        await rbacService.updateUser(selectedUser.id, updateData);
        showNotification('Usuário atualizado com sucesso', 'success');
      } else {
        // Criar usuário
        await rbacService.createUser(formData);
        showNotification('Usuário criado com sucesso', 'success');
      }
      
      handleCloseDialog();
      loadData();
    } catch (err) {
      showNotification(err.message, 'error');
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (window.confirm('Tem certeza que deseja desativar este usuário?')) {
      try {
        await rbacService.deactivateUser(userId);
        showNotification('Usuário desativado com sucesso', 'success');
        loadData();
      } catch (err) {
        showNotification(err.message, 'error');
      }
    }
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.display_name : 'Sem role';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Gerenciamento de Usuários
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Novo Usuário
          </Button>
        </Box>

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Último Login</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.full_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{getRoleName(user.role_id)}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Ativo' : 'Inativo'}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.last_login 
                        ? new Date(user.last_login).toLocaleString('pt-BR')
                        : 'Nunca'
                      }
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleOpenDialog(user)}
                        size="small"
                        title="Editar usuário"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleForceLogout(user.id, user.full_name)}
                        size="small"
                        color="warning"
                        title="Forçar logout (atualizar permissões)"
                      >
                        <LogoutIcon />
                      </IconButton>
                      {user.is_active && (
                        <IconButton
                          onClick={() => handleDeactivateUser(user.id)}
                          size="small"
                          color="error"
                          title="Desativar usuário"
                        >
                          <PersonOffIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Dialog para criar/editar usuário */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {selectedUser ? 'Editar Usuário' : 'Novo Usuário'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!!selectedUser}
                margin="normal"
                required
              />
              
              <TextField
                fullWidth
                label="Nome Completo"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                margin="normal"
                required
              />
              
              {!selectedUser && (
                <TextField
                  fullWidth
                  label="Senha"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  margin="normal"
                  required
                  helperText="Mínimo 8 caracteres"
                />
              )}
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role_id}
                  onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                  label="Role"
                >
                  <MenuItem value="">
                    <em>Nenhuma</em>
                  </MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Usuário Ativo"
                sx={{ mt: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              Cancelar
            </Button>
            <Button 
              onClick={() => runSubmit(handleSaveUser)}
              variant="contained"
              disabled={submitting || !formData.email || !formData.full_name || (!selectedUser && !formData.password)}
            >
              {selectedUser ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default UserManagement;